import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = "September 16, 2025"

  return (
    <>
      <Helmet>
        <title>Privacy Policy | FGA Prestige - Data Protection</title>
        <meta name="description" content="Learn how FGA Prestige protects your personal information and privacy in our luxury fashion services." />
        <meta name="keywords" content="FGA Prestige privacy, data protection, personal information, privacy policy, GDPR compliance" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <main className="min-h-screen py-20 bg-white">
        <div className="fga-container">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="fga-title text-3xl md:text-4xl mb-6" style={{ color: 'var(--fga-title-gold-dark)' }}>
                Privacy Policy
              </h1>
              <p className="fga-paragraph text-lg">
                Your Privacy and Data Protection at FGA Prestige
              </p>
              <div className="text-sm mt-4" style={{ color: 'var(--fga-paragraph-grey)' }}>
                Last Updated: {lastUpdated}
              </div>
            </div>

            {/* Content */}
            <div className="bg-white shadow-lg p-8 md:p-12 space-y-8">
              
              {/* Introduction */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Introduction
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    Welcome to the FGA Prestige Privacy Notice. FGA Prestige values your privacy and is committed to safeguarding your personal data. This notice explains how we collect, use, and protect your personal information, your rights regarding data privacy, and how the law protects you.
                  </p>
                  <p>
                    This notice is presented in a structured format so you can navigate directly to the sections most relevant to you. A Glossary is also available to clarify specific terms.
                  </p>
                  <div className="bg-gray-50 p-6 space-y-2">
                    <p><strong>Navigation Menu:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Important Information and Who We Are</li>
                      <li>How We Collect Your Personal Data</li>
                      <li>The Data We Collect About You</li>
                      <li>How We Use Your Personal Data</li>
                      <li>Disclosures of Your Personal Data</li>
                      <li>International Transfers</li>
                      <li>Data Security</li>
                      <li>Data Retention</li>
                      <li>Your Legal Rights</li>
                      <li>Glossary</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Important Information and Who We Are */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Important Information and Who We Are
                </h2>
                <div className="fga-paragraph space-y-4">
                  <h3 className="text-lg font-semibold">Purpose of this Privacy Notice:</h3>
                  <p>
                    This privacy notice provides information on how FGA Prestige collects and processes your personal data when you use our website, engage our services, or interact with us—for example, when subscribing to our updates, making a service request, or participating in our client activities.
                  </p>
                  <p>
                    This website and our services are not intended for children.
                  </p>
                  <p>
                    Please read this notice together with any other privacy or processing statements that we may provide when collecting your information, so you are fully aware of how and why your data is used. This privacy notice complements other notices we provide and does not replace them.
                  </p>
                  
                  <h3 className="text-lg font-semibold">Controller</h3>
                  <p>
                    FGA Prestige operates under FGA Group of Companies. References to "FGA Prestige," "we," "us," or "our" in this notice refer to the entity responsible for handling your personal data.
                  </p>
                  <p>
                    We have appointed a Data Protection Officer (DPO) responsible for addressing any questions related to this privacy notice.
                  </p>
                  
                  <div className="bg-gray-50 p-6 space-y-2">
                    <p><strong>Contact details:</strong></p>
                    <div><strong>Legal Entity:</strong> FGA Prestige Limited</div>
                    <div><strong>DPO Contact:</strong> Data Protection Office, FGA Group</div>
                    <div><strong>Email:</strong> privacy@fgaprestige.com</div>
                    <div><strong>Address:</strong> 19/F, Queen's Road Centre, 152 Queen's Road Central, Central, Hong Kong</div>
                    <div><strong>Telephone:</strong> (+852) 2207 3191</div>
                  </div>
                  
                  <p>
                    You have the right to lodge a complaint with the Office of the Privacy Commissioner for Personal Data, Hong Kong (PCPD). However, we encourage you to contact us first so we can address your concerns directly.
                  </p>
                  
                  <h3 className="text-lg font-semibold">Changes to This Privacy Notice</h3>
                  <p>
                    This notice was last updated on 16 September 2025. We may update it periodically and will notify you of significant changes. It is important that the personal data we hold about you is current and accurate, so please keep us informed of any updates.
                  </p>
                  
                  <h3 className="text-lg font-semibold">Third-Party Links</h3>
                  <p>
                    Our website may contain links to third-party websites, tools, or plugins. Clicking these may allow third parties to collect or share your data. We do not control these external websites, and we encourage you to review their privacy notices before providing any personal information.
                  </p>
                </div>
              </section>

              {/* How We Collect Your Personal Data */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  How We Collect Your Personal Data
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>We may obtain your personal data through the following methods:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Direct interactions</strong> – when you request services, complete forms, correspond with us, or provide data by phone, email, or otherwise.</li>
                    <li><strong>Automated technologies</strong> – as you interact with our website, we may collect technical data (IP address, browsing information, usage behavior). This is collected via cookies or analytics software, subject to your consent.</li>
                    <li><strong>Third parties and public sources</strong> – including analytics providers, business registries, and professional directories.</li>
                  </ul>
                </div>
              </section>

              {/* The Data We Collect About You */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  The Data We Collect About You
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>The information we collect may include:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Identity Data</strong> – name, title, date of birth, gender</li>
                    <li><strong>Contact Data</strong> – email, phone, billing and service addresses</li>
                    <li><strong>Financial Data</strong> – payment details, bank account information</li>
                    <li><strong>Transaction Data</strong> – details of your service usage and billing history</li>
                    <li><strong>Technical Data</strong> – login credentials, browser type, IP address, operating system</li>
                    <li><strong>Profile Data</strong> – membership ID, preferences, interests, feedback surveys</li>
                    <li><strong>Marketing Data</strong> – your preferences for receiving communication from us</li>
                    <li><strong>Usage Data</strong> – how you navigate our website and interact with services</li>
                  </ul>
                  <p>
                    We may also gather aggregated or anonymised data for analysis that does not reveal individual identities.
                  </p>
                  <p>
                    FGA Prestige will not collect sensitive data (e.g., health, race, religious beliefs) unless necessary for your services and with explicit consent.
                  </p>
                </div>
              </section>

              {/* How We Use Your Personal Data */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  How We Use Your Personal Data
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>Your data will only be used where legally permitted, including:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>To perform a contract with you (e.g., concierge service or membership agreement)</li>
                    <li>To pursue our legitimate business interests while ensuring they do not conflict with your fundamental rights</li>
                    <li>To comply with legal or regulatory obligations</li>
                    <li>To improve our website, services, and client experience through analytics</li>
                  </ul>
                  <p>
                    You may withdraw consent for marketing communications or website cookies at any time by contacting us or adjusting preferences in our cookie banner.
                  </p>
                </div>
              </section>

              {/* Disclosures of Your Personal Data */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Disclosures of Your Personal Data
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>We may share your personal data with:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Internal FGA Group entities</li>
                    <li>Trusted service or technology providers that help us deliver services</li>
                    <li>Professional advisers such as bankers, lawyers, or auditors</li>
                    <li>Regulators, where required by law</li>
                    <li>In rare cases, parties involved in a merger, acquisition, or restructuring of FGA Group</li>
                  </ul>
                  <p>
                    We require all partners and providers to treat your data lawfully and securely.
                  </p>
                </div>
              </section>

              {/* International Transfers */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  International Transfers
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    As FGA Prestige serves clients worldwide, your personal data may be transferred outside of Hong Kong, including regions without equivalent data protection laws. In such cases, we ensure safeguards such as:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Only transferring to jurisdictions with recognised adequate protection</li>
                    <li>Using contractual safeguards to protect your data</li>
                    <li>Sharing only with partners who commit to privacy compliance</li>
                  </ul>
                </div>
              </section>

              {/* Data Security */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Data Security
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    We maintain stringent security protocols to prevent accidental loss, misuse, or unauthorised access to your personal data. Access is restricted to employees or partners with a legitimate business need, subject to confidentiality obligations.
                  </p>
                  <p>
                    If a data breach occurs, we will notify you and the relevant regulatory authority as legally required.
                  </p>
                </div>
              </section>

              {/* Data Retention */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Data Retention
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    We will only retain your personal data for as long as necessary to meet our service obligations, regulatory requirements, or legal claims. Examples:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Basic client records are retained for at least six years for compliance purposes.</li>
                    <li>Website analytics data may be stored for one year.</li>
                    <li>In some cases, anonymised data is kept for research or statistical use without further notice.</li>
                  </ul>
                </div>
              </section>

              {/* Your Legal Rights */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Your Legal Rights
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>You may exercise rights regarding your personal data at any time, including the right to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Access the data we hold about you</li>
                    <li>Request correction of inaccuracies</li>
                    <li>Request deletion of your data (subject to regulatory retention obligations)</li>
                    <li>Object to data use for marketing or legitimate interest purposes</li>
                    <li>Restrict processing under certain conditions</li>
                    <li>Request transfer of your data to another entity</li>
                    <li>Withdraw previously given consent</li>
                  </ul>
                  <p>
                    Requests can be submitted at: info@fgaprestige.com.
                  </p>
                  <p>
                    No fees are charged for exercising your rights, except in cases of excessive or unfounded requests.
                  </p>
                </div>
              </section>

              {/* Glossary */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Glossary
                </h2>
                <div className="fga-paragraph space-y-4">
                  <div className="space-y-3">
                    <p><strong>Legitimate Interest</strong> – The operational or business need to use your data in ways that improve service quality, security, or business growth without compromising your rights.</p>
                    <p><strong>Performance of Contract</strong> – Data processing necessary to fulfill our obligations to you as part of a service agreement.</p>
                    <p><strong>Regulatory Obligation</strong> – Data use required for compliance with legal frameworks in Hong Kong or abroad.</p>
                  </div>
                </div>
              </section>
            </div>

            {/* Navigation */}
            <div className="mt-12 text-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/terms"
                  className="fga-btn-primary"
                >
                  View Terms & Conditions
                </Link>
                <Link
                  to="/contact"
                  className="fga-btn-primary"
                >
                  Contact Privacy Team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default PrivacyPolicyPage